/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { KeyboardEvent, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { If, Then, Else } from "react-if";
import { z } from "zod";
import {
  Box,
  theme,
  Text,
  Button,
  FlexContainer,
  Textarea,
  Spacer,
  Accordion,
  AccordionHeader,
  AccordionItem,
  List,
} from "@nordcloud/gnui";
import { FormGroup, safeStr } from "~/components/Forms";
import { useGetResourcesByTags } from "~/hooks";
import { isNotEmpty, inflect } from "~/tools";
import { Item } from "~/types";
import { ResourceGroupExpressionDelete } from "./ResourceGroupExpressionDelete";

type Props = {
  onSubmit?: SubmitHandler<{
    expression: string;
  }>;
  handleSave?: (expression?: string) => void;
  defaultValue?: string;
  handleDynamicUpdate: (dynamicItems: Item[]) => void;
  setExpression?: (expression?: string) => void;
  onPageInfoChange?: (pageInfo?: { count?: number; pages?: number }) => void;
  limit?: number;
  disableExpressionDeleteModal?: boolean;
};

export const schema = z.object({
  expression: safeStr.optional(),
});

export function ResourceGroupExpression({
  onSubmit,
  handleSave,
  defaultValue,
  handleDynamicUpdate,
  setExpression,
  onPageInfoChange,
  limit,
  disableExpressionDeleteModal = false,
}: Props) {
  const [isEditMode, setEditMode] = useState(false);
  const [isSaveDisabled, setDisableSave] = useState(false);
  const [dynamicResourceCount, setDynamicResourceCount] = useState(0);

  const { handleSubmit, register, formState, watch, reset } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      expression: defaultValue ?? "",
    },
  });

  const expression = watch("expression");

  const onClose = () => {
    setEditMode(false);
    setDisableSave(false);
    reset();
  };

  const onSuccess = (
    responseData?: Item[],
    pageInfo?: { count?: number; pages?: number }
  ) => {
    setDynamicResourceCount(pageInfo?.count ?? 0);
    setExpression?.(expression);
    onSubmit?.({ expression });
    handleDynamicUpdate(responseData ?? []);
    onPageInfoChange?.(pageInfo);
    setEditMode(false);
    setDisableSave(false);
  };

  const { getResourcesByTags } = useGetResourcesByTags({
    onSuccess: onSuccess,
    onError: () => setDisableSave(false),
  });

  const onSave = (data: { expression: string }) => {
    setDisableSave(true);
    if (isNotEmpty(data.expression)) {
      getResourcesByTags({
        variables: {
          tagsExpression: data.expression,
          page: 1,
          limit: limit,
        },
      });
      handleSave?.(expression);
    } else {
      setExpression?.(undefined);
      handleSave?.(undefined);
      handleDynamicUpdate([]);
      setEditMode(false);
      setDisableSave(false);
    }
  };

  const onExpressionKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key !== "Enter" ||
      isSaveDisabled ||
      event.nativeEvent.isComposing
    ) {
      return;
    }

    event.preventDefault();
    handleSubmit(onSave)();
  };

  useEffect(() => {
    reset({ expression: defaultValue });
  }, [isEditMode, defaultValue, reset]);

  useEffect(() => {
    if (defaultValue) {
      getResourcesByTags({
        variables: {
          tagsExpression: defaultValue,
          page: 1,
          limit: limit,
        },
      });
    }
  }, [defaultValue, getResourcesByTags, limit]);

  return (
    <Box boxStyle="lightGrey" spacing="spacing04">
      <If condition={!isEditMode}>
        <Then>
          <FlexContainer direction="row" justifyContent="space-between">
            <div>
              <Text tag="div" size="sm" color={theme.color.text.text02}>
                Expression
              </Text>
              <Spacer height={theme.spacing.spacing04} />
              <If condition={!!defaultValue}>
                <Then>
                  <Text
                    tag="div"
                    color={
                      defaultValue
                        ? theme.color.text.text01
                        : theme.color.text.text02
                    }
                  >
                    {defaultValue}
                  </Text>
                  <Text tag="div" size="sm" color={theme.color.text.text02}>
                    {dynamicResourceCount}
                    {` ${inflect("resource")(dynamicResourceCount)}`} found
                  </Text>
                </Then>
                <Else>
                  <Button
                    ml="auto"
                    severity="low"
                    type="button"
                    icon="plus"
                    onClick={() => setEditMode(true)}
                  >
                    Add Expression
                  </Button>
                </Else>
              </If>
            </div>
            <If condition={!!defaultValue}>
              <Then>
                <FlexContainer direction="row">
                  <Button
                    data-testid="edit-expression"
                    onClick={() => setEditMode(true)}
                    icon="edit"
                    severity="low"
                  />
                  <Spacer width={theme.spacing.spacing04} />
                  <ResourceGroupExpressionDelete
                    onDelete={onSave}
                    disableDeleteModal={disableExpressionDeleteModal}
                  />
                </FlexContainer>
              </Then>
            </If>
          </FlexContainer>
        </Then>
        <Else>
          <FlexContainer
            direction="row"
            justifyContent="space-between"
            alignItems="start"
          >
            <div>
              <Text tag="div" size="sm" color={theme.color.text.text02}>
                Expression
              </Text>
            </div>
            <FlexContainer direction="row">
              <Button disabled={isSaveDisabled} onClick={handleSubmit(onSave)}>
                Save
              </Button>
              <Spacer width={theme.spacing.spacing04} />
              <Button onClick={onClose} icon="close" severity="low" />
            </FlexContainer>
          </FlexContainer>
          <Spacer height={theme.spacing.spacing04} />
          <FormGroup
            error={formState.errors["expression"]}
            customCss={{ marginBottom: 0 }}
          >
            <Textarea
              data-testid="expression-input"
              placeholder={`!environment=prod AND name=linux_vm AND (region=eu1,eu2 OR group="g@1","g-1")`}
              {...register("expression")}
              onKeyDown={onExpressionKeyDown}
              rows={2}
            />
          </FormGroup>
          <Spacer height={theme.spacing.spacing04} />
          <Accordion>
            <AccordionHeader title="How to Write an Expression">
              <AccordionItem>
                <Text
                  size="md"
                  color={theme.color.text.text01}
                  mb={theme.spacing.spacing03}
                >
                  Expressions follow specific rules:
                </Text>
                <List
                  items={[
                    {
                      title:
                        "1. Tags are represented in key=value format and combined using case-insensitive logical operators: AND (&), OR (|), and NOT (!)",
                    },
                    {
                      title:
                        "2. Keys are limited to alphanumeric characters and symbols such as: +-._:/@",
                    },
                    {
                      title:
                        "3. Values can contain any characters, but if they include anything beyond alphanumeric and _, they must be enclosed in quotation marks",
                    },
                    {
                      title:
                        "4. Lists of values can be separated by commas, e.g., key=value1,value2 — this syntax is equivalent to key=value1 OR key=value2",
                    },
                  ]}
                />
                <Text
                  size="md"
                  color={theme.color.text.text01}
                  mt={theme.spacing.spacing03}
                >
                  For a comprehensive understanding of this functionality,
                  please refer to the documentation.
                </Text>
              </AccordionItem>
            </AccordionHeader>
          </Accordion>
        </Else>
      </If>
    </Box>
  );
}
